<script setup lang="ts">
import { computed } from 'vue'
import type { TourStep, TourPlacement } from '../types/tour.types'
import { renderTourMarkdown } from '../composables/tour-markdown'

const props = defineProps<{
  step: TourStep
  stepIndex: number
  totalSteps: number
  isFirst: boolean
  isLast: boolean
  placement: TourPlacement
  pinned: boolean
  tourTitle: string
}>()

const emit = defineEmits<{
  (e: 'next'): void
  (e: 'prev'): void
  (e: 'skip'): void
  (e: 'close'): void
  (e: 'repeat'): void
  (e: 'restart'): void
  (e: 'execute-ai', prompt: string): void
  (e: 'disable-training'): void
}>()

const bodyHtml = computed(() => renderTourMarkdown(props.step.body))
const showArrow = computed(() => !props.pinned && props.placement !== 'auto')
</script>

<template>
  <div class="tour-popover" role="dialog" aria-modal="false" :aria-label="step.title">
    <span
      v-if="showArrow"
      class="tour-popover__arrow"
      :class="`tour-popover__arrow--${placement}`"
      aria-hidden="true"
    />

    <header class="tour-popover__head">
      <span class="tour-popover__eyebrow">{{ tourTitle }}</span>
      <div class="tour-popover__head-actions">
        <button
          type="button"
          class="tour-popover__icon-btn"
          title="Repeat this step"
          aria-label="Repeat this step"
          @click="emit('repeat')"
        >
          <i class="fa-solid fa-rotate-right" />
        </button>
        <button
          type="button"
          class="tour-popover__icon-btn"
          title="Restart tour"
          aria-label="Restart tour"
          @click="emit('restart')"
        >
          <i class="fa-solid fa-backward-fast" />
        </button>
        <button
          type="button"
          class="tour-popover__icon-btn"
          title="Close"
          aria-label="Close tour"
          @click="emit('close')"
        >
          <i class="fa-solid fa-xmark" />
        </button>
      </div>
    </header>

    <h3 class="tour-popover__title">{{ step.title }}</h3>
    <!-- Content is authored in trusted config/.md, rendered via the in-house formatter. -->
    <!-- eslint-disable-next-line vue/no-v-html -->
    <div class="tour-popover__body" v-html="bodyHtml" />

    <button
      v-if="step.aiAction"
      type="button"
      class="tour-popover__ai"
      @click="emit('execute-ai', step.aiAction.prompt)"
    >
      <i class="fa-solid fa-wand-magic-sparkles" />
      {{ step.aiAction.label ?? 'Execute with AI' }}
    </button>

    <div class="tour-popover__progress" aria-hidden="true">
      <span
        v-for="n in totalSteps"
        :key="n"
        class="tour-popover__seg"
        :class="{ 'tour-popover__seg--on': n <= stepIndex + 1 }"
      />
    </div>

    <footer class="tour-popover__foot">
      <span class="tour-popover__count">Step {{ stepIndex + 1 }} of {{ totalSteps }}</span>
      <div class="tour-popover__foot-actions">
        <button
          v-if="!isFirst"
          type="button"
          class="tour-btn tour-btn--ghost"
          @click="emit('prev')"
        >
          <i class="fa-solid fa-arrow-left" /> Back
        </button>
        <button v-if="!isLast" type="button" class="tour-btn tour-btn--text" @click="emit('skip')">
          Skip
        </button>
        <button type="button" class="tour-btn tour-btn--primary" @click="emit('next')">
          {{ isLast ? 'Finish' : 'Next' }}
          <i :class="isLast ? 'fa-solid fa-check' : 'fa-solid fa-arrow-right'" />
        </button>
      </div>
    </footer>

    <!-- Opt out of the guided tours on every screen (the help button can turn them back on). -->
    <button
      type="button"
      class="tour-popover__optout"
      data-testid="tour-disable-training"
      @click="emit('disable-training')"
    >
      <i class="fa-solid fa-toggle-off" /> Turn off training mode
    </button>
  </div>
</template>

<style scoped lang="scss">
.tour-popover {
  position: relative;
  width: min(360px, calc(100vw - 32px));
  background: rgb(var(--v-theme-surface));
  color: rgb(var(--v-theme-on-surface));
  border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
  border-radius: 16px;
  box-shadow: 0 12px 40px rgba(17, 29, 43, 0.22);
  padding: 18px 20px;
  animation: tour-pop-in 250ms cubic-bezier(0.16, 1, 0.3, 1) both;

  &__head {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 10px;
  }

  &__eyebrow {
    font-size: 0.68rem;
    font-weight: 700;
    letter-spacing: 0.1em;
    text-transform: uppercase;
    color: rgb(var(--v-theme-primary));
  }

  &__head-actions {
    display: flex;
    gap: 2px;
  }

  &__icon-btn {
    width: 26px;
    height: 26px;
    border-radius: 8px;
    background: transparent;
    color: rgba(var(--v-theme-on-surface), 0.55);
    font-size: 0.78rem;
    cursor: pointer;
    transition: background 150ms ease, color 150ms ease;

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.06);
      color: rgb(var(--v-theme-on-surface));
    }

    &:focus-visible {
      outline: 2px solid rgba(137, 42, 84, 0.45);
      outline-offset: 1px;
    }
  }

  &__title {
    font-size: 1.12rem;
    font-weight: 700;
    line-height: 1.25;
    margin-bottom: 6px;
  }

  &__body {
    font-size: 0.9rem;
    line-height: 1.5;
    color: rgba(var(--v-theme-on-surface), 0.78);

    :deep(p) {
      margin: 0 0 8px;
    }
    :deep(p:last-child) {
      margin-bottom: 0;
    }
    :deep(ul) {
      margin: 4px 0 8px;
      padding-left: 18px;
    }
    :deep(li) {
      margin-bottom: 3px;
    }
    :deep(code) {
      font-family: 'Geist Mono Variable', monospace;
      font-size: 0.82em;
      background: rgba(var(--v-theme-on-surface), 0.07);
      padding: 1px 5px;
      border-radius: 5px;
    }
    :deep(a) {
      color: rgb(var(--v-theme-primary));
    }
  }

  &__ai {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 14px;
    padding: 9px 16px;
    border-radius: 10px;
    font-size: 0.85rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(120deg, #46c8de, #892a54);
    cursor: pointer;
    transition: transform 150ms ease, box-shadow 150ms ease;

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 8px 18px rgba(137, 42, 84, 0.24);
    }
    &:focus-visible {
      outline: 2px solid rgba(137, 42, 84, 0.45);
      outline-offset: 2px;
    }
  }

  &__progress {
    display: flex;
    gap: 4px;
    margin: 18px 0 12px;
  }

  &__seg {
    flex: 1;
    height: 4px;
    border-radius: 999px;
    background: rgba(var(--v-theme-on-surface), 0.12);
    transition: background 300ms ease;

    &--on {
      background: rgb(var(--v-theme-primary));
    }
  }

  &__foot {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
  }

  // Quiet opt-out under the actions: visible on every step, never competing with Next.
  &__optout {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    margin-top: 12px;
    padding: 0;
    border: 0;
    background: none;
    font-size: 0.74rem;
    color: rgba(var(--v-theme-on-surface), 0.6);
    cursor: pointer;

    &:hover,
    &:focus-visible {
      color: rgb(var(--v-theme-on-surface));
      text-decoration: underline;
    }
  }

  &__count {
    font-size: 0.74rem;
    color: rgba(var(--v-theme-on-surface), 0.5);
    white-space: nowrap;
  }

  &__foot-actions {
    display: flex;
    align-items: center;
    gap: 6px;
  }

  // Arrow
  &__arrow {
    position: absolute;
    width: 12px;
    height: 12px;
    background: rgb(var(--v-theme-surface));
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));
    transform: rotate(45deg);

    &--bottom {
      top: -7px;
      left: 50%;
      margin-left: -6px;
      border-right: none;
      border-bottom: none;
    }
    &--top {
      bottom: -7px;
      left: 50%;
      margin-left: -6px;
      border-left: none;
      border-top: none;
    }
    &--end {
      left: -7px;
      top: 50%;
      margin-top: -6px;
      border-top: none;
      border-right: none;
    }
    &--start {
      right: -7px;
      top: 50%;
      margin-top: -6px;
      border-bottom: none;
      border-left: none;
    }
  }
}

.tour-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border-radius: 9px;
  padding: 8px 14px;
  font-size: 0.85rem;
  font-weight: 600;
  cursor: pointer;
  transition: background 150ms ease, transform 150ms ease, box-shadow 150ms ease;

  i {
    font-size: 0.72rem;
  }

  &--primary {
    color: #fff;
    background: rgb(var(--v-theme-primary));

    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 6px 16px rgba(137, 42, 84, 0.26);
    }
  }

  &--ghost {
    color: rgba(var(--v-theme-on-surface), 0.7);
    background: transparent;
    border: 1px solid rgba(var(--v-border-color), var(--v-border-opacity));

    &:hover {
      background: rgba(var(--v-theme-on-surface), 0.04);
    }
  }

  &--text {
    color: rgba(var(--v-theme-on-surface), 0.5);
    background: transparent;

    &:hover {
      color: rgba(var(--v-theme-on-surface), 0.8);
    }
  }

  &:focus-visible {
    outline: 2px solid rgba(137, 42, 84, 0.45);
    outline-offset: 2px;
  }
}

@keyframes tour-pop-in {
  from {
    opacity: 0;
    transform: scale(0.96) translateY(6px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

@media (prefers-reduced-motion: reduce) {
  .tour-popover {
    animation: none;
  }
}
</style>
